<template>
	<div
		class="app-wapi-front"
		:style="
			vue_app_theme === THEMES
				? '--theme-color: rgb(37, 211, 102)'
				: '--theme-color: rgb(40, 167, 232)'
		"
	>
		<!-- //////////////// -->
		<!-- HEADER -->
		<div class="app-wapi-front__header">
			<HeaderMobile
				v-if="device.isMobile"
				:channels="channels"
				:is-notify="isNotify"
			/>
			<HeaderDesktop
				v-else
				:balance="formatBalance(balance)"
				:channels="channels"
				:is-notify="isNotify"
			/>
		</div>
		<router-view />
	</div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapActions, mapGetters } from "vuex";
import HeaderMobile from "@/components/header/HeaderMobile.vue";
import HeaderDesktop from "@/components/header/HeaderDesktop.vue";

@Options({
	components: { HeaderDesktop, HeaderMobile },

	computed: {
		...mapGetters("instanceModule", {
			device: "getDevice",
		}),
		...mapGetters("authModule", {
			checkAuthGetters: "checkAuthGetters",
		}),
		...mapGetters("paymentModule", {
			payActivatedChannels: "payActivatedChannelsGetters",
			payGetBalance: "payGetBalanceGetters",
		}),
	},
	methods: {
		...mapActions("instanceModule", ["deviceTypeAction"]),
		...mapActions("authModule", ["checkAuthAction"]),
		...mapActions("paymentModule", [
			"payActivatedChannelsAction",
			"payChannelDetailAction",
			"payGetBalanceAction",
		]),
	},

	watch: {
		authenticate(data: { isAuth: boolean; route: string }) {
			this.routeRestriction(data);
		},
	},
})
export default class App extends Vue {
	public INNER_WIDTH = 773;
	public isNotify = true;

	public THEMES: "wapi" | "tapi" = "wapi";
	public vue_app_theme = "";

	public isAuth = false;

	// ************************************************************
	// Define mapGetters
	public device: { isMobile: boolean };
	public checkAuthGetters: { result: number; isAdmin: boolean };
	public payActivatedChannels: { count: number };
	public payGetBalance: number | null;

	// ************************************************************
	// Define mapActions
	public deviceTypeAction!: (payload: { isMobile: boolean }) => void;
	public checkAuthAction!: () => void;
	public payActivatedChannelsAction!: () => Promise<void>;
	public payGetBalanceAction!: () => Promise<void>;
	public payChannelDetailAction!: () => Promise<void>;

	/**
	 * Hook created
	 */
	public created() {
		this.vue_app_theme = process.env.VUE_APP_THEME;
	}

	/**
	 * Hook mounted
	 */
	public async mounted() {
		window.addEventListener("resize", this.getInnerWidth);
		this.getInnerWidth();
		await this.checkAuthAction();
		await this.payActivatedChannelsAction();
		await this.payGetBalanceAction();
		await this.payChannelDetailAction();
	}

	/**
	 * Возвращает значение баланса
	 */
	public get balance(): number {
		return this.payGetBalance !== null ? this.payGetBalance : 0;
	}

	/**
	 * Количество каналов
	 */
	public get channels() {
		return this.payActivatedChannels.count;
	}

	/**
	 * Переход на другую страницу
	 * @param routeName
	 * @param id
	 */
	public redirect(routeName: string, id?: number) {
		this.$router.push({ name: routeName, params: { id: id } });
	}

	/**
	 * Ширина окна
	 */
	public getInnerWidth() {
		this.deviceTypeAction({
			isMobile: window.innerWidth < this.INNER_WIDTH,
		});
	}

	/**
	 * Форматирование цены
	 * @param balance
	 */
	public formatBalance(balance: number): string {
		return Intl.NumberFormat("ru-Ru", {
			currency: "RUB",
			style: "currency",
		}).format(balance);
	}

	/**
	 * Computed
	 * return boolean
	 * true is authenticated
	 */
	public get authenticate() {
		let route;
		if (this.checkAuthGetters?.result !== undefined) {
			this.isAuth = this.checkAuthGetters.result !== 0;
			route = this.$route.name;
		}
		return { isAuth: this.isAuth, route: route };
	}

	/**
	 * Ограничение по роутам
	 * @param data
	 */
	public routeRestriction(data: { isAuth: boolean; route: string }) {
		if (data.isAuth) {
			// Авторизованы
			if (
				// Запрещаем заходить по этим рутам
				data.route === "registration" ||
				data.route === "login" ||
				data.route === "confirmation"
			) {
				// Если перечисленные роуты совпадают, тогда перенаправляем на instances
				this.redirect("instances");
			}
		} else {
			// Не авторизованы
			if (
				// Разрешаем заходить по этим рутам
				data.route === "registration" ||
				data.route === "login" ||
				data.route === "confirmation"
			) {
				this.redirect(data.route);
			} else {
				// Если перечисленные роуты не совпадают, тогда перенаправляем на login
				this.redirect("login");
			}
		}
	}
}
</script>

<style lang="scss">
.app-wapi-front {
	&__header {
		background: var(--theme-color);
	}
}
</style>
